import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Clock, AlertTriangle, CheckCircle } from 'lucide-react';

interface Subscription {
  id: string;
  ends_at: string;
  is_active: boolean;
  duration_days: number;
}

export default function SubscriptionIndicator() {
  const { user, profile } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadSubscription();
    }
  }, [user?.id, profile?.organization_id]);

  const loadSubscription = async () => {
    try {
      // Check user subscription first
      let { data: userSub } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user?.id)
        .eq('is_active', true)
        .gte('ends_at', new Date().toISOString())
        .order('ends_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (userSub) {
        setSubscription(userSub);
        setLoading(false);
        return;
      }

      // Check organization subscription if user has org
      if (profile?.organization_id) {
        const { data: orgSub } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('organization_id', profile.organization_id)
          .eq('is_active', true)
          .gte('ends_at', new Date().toISOString())
          .order('ends_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (orgSub) {
          setSubscription(orgSub);
        }
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysRemaining = (): number => {
    if (!subscription) return 0;
    const endsAt = new Date(subscription.ends_at);
    const now = new Date();
    const diff = endsAt.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getStatusColor = (): string => {
    const days = getDaysRemaining();
    if (days <= 3) return 'text-red-500 bg-red-50 dark:bg-red-900/20';
    if (days <= 7) return 'text-orange-500 bg-orange-50 dark:bg-orange-900/20';
    return 'text-green-500 bg-green-50 dark:bg-green-900/20';
  };

  const getStatusIcon = () => {
    const days = getDaysRemaining();
    if (days <= 3) return <AlertTriangle size={14} />;
    if (days <= 7) return <Clock size={14} />;
    return <CheckCircle size={14} />;
  };

  const formatDaysText = (days: number): string => {
    if (days === 1) return '1 день';
    if (days >= 2 && days <= 4) return `${days} дня`;
    return `${days} дней`;
  };

  if (loading) {
    return null;
  }

  if (!subscription) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium text-red-500 bg-red-50 dark:bg-red-900/20">
        <AlertTriangle size={14} />
        <span>Нет подписки</span>
      </div>
    );
  }

  const daysRemaining = getDaysRemaining();

  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor()}`}>
      {getStatusIcon()}
      <span>
        {daysRemaining > 0 
          ? `Подписка: ${formatDaysText(daysRemaining)}`
          : 'Подписка истекла'}
      </span>
    </div>
  );
}
